/** Blog-style scenario articles offering freelance advice, client stories, and career guides for creatives. */
export const scenarioPosts = [
  {
    id: "blog-1",
    slug: "how-i-handled-a-client-who-refused-to-pay",
    title: "How I Handled a Client Who Refused to Pay",
    excerpt: "After delivering a full brand identity project, my client went silent. Here's the exact steps I took to recover my payment — and what I'd do differently.",
    author: "Kreator Editorial",
    date: "March 12, 2026",
    readTime: "6 min read",
    pinned: true,
    category: "Media Production",
    tags: ["Payment", "Legal", "Contracts"],
    content: [
      {
        type: "paragraph",
        text: "It was week three after final delivery. The files were sent, the client had approved everything, and then — nothing. No payment, no reply, no acknowledgement. If you've freelanced for more than a year, you've probably been here."
      },
      {
        type: "heading",
        text: "Step 1: Document Everything First"
      },
      {
        type: "paragraph",
        text: "Before sending a single follow-up, I compiled every email, message, and approval into a single PDF. Screenshots of WhatsApp approvals, email threads, the signed contract — all of it. This is your evidence package. You want it ready before you escalate."
      },
      {
        type: "heading",
        text: "Step 2: Send a Formal Payment Reminder"
      },
      {
        type: "paragraph",
        text: "Not a casual 'hey just checking in' — a proper, dated invoice reminder referencing the contract clause on payment terms. Keep it professional and factual. State the amount, the due date, and the consequence (late fee, legal action) if unpaid within 7 days."
      },
      {
        type: "heading",
        text: "Step 3: Escalate Calmly"
      },
      {
        type: "paragraph",
        text: "If the 7 days pass, send a final notice stating you'll be pursuing the matter through small claims or a collections agency. In Singapore, the Small Claims Tribunal handles disputes up to SGD 20,000. Most clients pay before it gets there — the threat alone is usually enough."
      },
      {
        type: "heading",
        text: "What I'd Do Differently"
      },
      {
        type: "paragraph",
        text: "Require a 50% deposit upfront. No exceptions. I now also include a clause that ownership of deliverables only transfers upon full payment. That means if they don't pay, they legally can't use the work. It's a powerful lever."
      },
      {
        type: "tip",
        text: "Always use a contract — even for small jobs. A simple one-page agreement with scope, payment terms, and revision limits protects both parties and sets professional expectations from day one."
      }
    ]
  },
  {
    id: "blog-2",
    slug: "pricing-your-first-freelance-project",
    title: "Pricing Your First Freelance Project Without Underselling Yourself",
    excerpt: "Most beginners price too low out of fear. This is the framework I use to price confidently — even with no prior clients.",
    author: "Kreator Editorial",
    date: "February 28, 2026",
    readTime: "8 min read",
    pinned: true,
    category: "UX/UI & Web Design",
    tags: ["Pricing", "Business", "Beginner"],
    content: [
      {
        type: "paragraph",
        text: "The most common mistake new freelancers make isn't bad work — it's bad pricing. They charge $50 for a logo that took 12 hours, then wonder why they're burning out. Pricing is a skill, and like any skill, it can be learned."
      },
      {
        type: "heading",
        text: "Start With Your Hourly Floor"
      },
      {
        type: "paragraph",
        text: "Calculate the minimum hourly rate you need to survive. Take your monthly expenses, divide by the number of billable hours you can realistically work (usually 80–100 hours for part-timers), and that's your floor. Never go below it."
      },
      {
        type: "heading",
        text: "Research Market Rates"
      },
      {
        type: "paragraph",
        text: "Check Contra, Dribbble Jobs, and local Facebook groups to see what others charge for similar work. You don't need to match the top — but you should be in the same ballpark. Charging 10x below market signals inexperience, not value."
      },
      {
        type: "heading",
        text: "Price the Project, Not the Hours"
      },
      {
        type: "paragraph",
        text: "Clients don't care how long something takes — they care about the outcome. A logo rebrand that transforms a business is worth $800 whether it took you 4 hours or 20. Package your work by deliverable, not by time."
      },
      {
        type: "tip",
        text: "When in doubt, quote higher than you're comfortable with. You can always negotiate down. You can never negotiate up after sending a quote."
      },
      {
        type: "heading",
        text: "The Confidence Factor"
      },
      {
        type: "paragraph",
        text: "Clients read confidence in your pricing. A designer who says '$200 for a logo, or maybe $150, I'm flexible' sounds unsure of their value. A designer who says 'My brand identity packages start at $600' sounds like a professional. Be the second designer."
      }
    ]
  },
  {
    id: "blog-3",
    slug: "building-a-portfolio-from-zero",
    title: "Building a Portfolio From Zero: The Mock Project Method",
    excerpt: "No clients yet? No problem. Here's how to build a portfolio that looks professional before you've landed your first paid gig.",
    author: "Kreator Editorial",
    date: "January 15, 2026",
    readTime: "5 min read",
    category: "Visual Communication",
    tags: ["Portfolio", "Beginner", "Branding"],
    content: [
      {
        type: "paragraph",
        text: "Every creative professional faces the same paradox at the start: you need a portfolio to get clients, but you need clients to build a portfolio. The mock project method breaks this loop entirely."
      },
      {
        type: "heading",
        text: "What Is a Mock Project?"
      },
      {
        type: "paragraph",
        text: "A mock project is a self-initiated piece of work where you pick a real (or fictional) brand and redesign or create assets for it as if you were hired. The key is treating it with the same rigour as a paid job — brief, research, iterations, final delivery."
      },
      {
        type: "heading",
        text: "Pick Brands You Actually Use"
      },
      {
        type: "paragraph",
        text: "Redesign the app you use every day. Rebrand your favourite local café. Create a social media kit for a student club. These feel authentic because they are — you understand the audience and the context, which shows in the work."
      },
      {
        type: "heading",
        text: "Document the Process"
      },
      {
        type: "paragraph",
        text: "The case study matters as much as the final output. Show your research, your sketches, your rejected directions. Clients and employers want to see how you think, not just what you produce. A well-documented mock project beats a poorly-explained real one."
      },
      {
        type: "tip",
        text: "Aim for 3–5 strong, varied projects rather than 10 mediocre ones. Quality over quantity always wins in a creative portfolio."
      },
      {
        type: "heading",
        text: "Where to Host It"
      },
      {
        type: "paragraph",
        text: "Behance is the industry standard for visual work. Notion works well for case studies. If you're a web designer, build your own site — it's the ultimate portfolio piece. Whatever you choose, make sure it loads fast and looks clean on mobile."
      }
    ]
  },
  {
    id: "blog-4",
    slug: "managing-scope-creep-like-a-pro",
    title: "Managing Scope Creep Like a Pro",
    excerpt: "Scope creep is the silent killer of freelance profitability. Here's a practical system to stop it before it starts.",
    author: "Kreator Editorial",
    date: "March 28, 2026",
    readTime: "7 min read",
    pinned: true,
    category: "Media Production",
    tags: ["Scope Creep", "Contracts", "Client Management"],
    content: [
      {
        type: "paragraph",
        text: "It starts small. 'Can you just tweak the colour?' Then it's 'Can we add one more page?' Then suddenly you're three weeks past deadline doing work that was never in the original brief. Scope creep is the number one profitability killer for freelancers."
      },
      {
        type: "heading",
        text: "Define Scope in Writing Before You Start"
      },
      {
        type: "paragraph",
        text: "Your contract should list exactly what is included — and crucially, what is not. 'Logo design including 3 concepts, 2 rounds of revisions, and final files in SVG/PNG' is airtight. 'Logo design' is an invitation for endless requests."
      },
      {
        type: "heading",
        text: "Use a Change Order System"
      },
      {
        type: "paragraph",
        text: "When a client requests something outside scope, don't just say yes or no. Say: 'That's outside our current scope — I can put together a change order for that.' Then quote it. Most clients either drop the request or happily pay. Either outcome is fine."
      },
      {
        type: "tip",
        text: "Keep a running log of all client requests in a shared doc. When scope creep happens, you can point to the log and say 'this was added on X date, here's the additional cost.' It removes emotion from the conversation."
      },
      {
        type: "heading",
        text: "Set Revision Limits Explicitly"
      },
      {
        type: "paragraph",
        text: "Two rounds of revisions is standard for most creative work. State this in your contract and in your kickoff email. When a client hits their limit, remind them warmly but firmly: 'We've used both revision rounds included in the project. Additional revisions are billed at my hourly rate of $X.'"
      }
    ]
  },
  {
    id: "blog-5",
    slug: "writing-a-freelance-proposal-that-wins",
    title: "Writing a Freelance Proposal That Actually Wins",
    excerpt: "Most proposals lose before they're even read. Here's how to write one that gets opened, understood, and signed.",
    author: "Kreator Editorial",
    date: "April 1, 2026",
    readTime: "6 min read",
    category: "Visual Communication",
    tags: ["Proposals", "Client Management", "Business"],
    content: [
      {
        type: "paragraph",
        text: "A proposal isn't a quote. A quote says 'here's what it costs.' A proposal says 'here's what I understand about your problem, here's how I'll solve it, and here's what it costs.' That distinction is everything."
      },
      {
        type: "heading",
        text: "Lead With Their Problem, Not Your Skills"
      },
      {
        type: "paragraph",
        text: "The first paragraph of your proposal should reflect the client's situation back to them. 'You're launching a new product line and need a visual identity that speaks to a younger audience.' This shows you listened. Most freelancers skip this and jump straight to their credentials — a mistake."
      },
      {
        type: "heading",
        text: "Offer Three Tiers"
      },
      {
        type: "paragraph",
        text: "Give clients a choice: a lean option, a standard option, and a premium option. This anchors the conversation around which package fits, not whether to hire you at all. The middle tier is usually what gets chosen — price it accordingly."
      },
      {
        type: "heading",
        text: "Keep It Short"
      },
      {
        type: "paragraph",
        text: "A proposal should be readable in under 5 minutes. One page is ideal. Two pages maximum. If you need more, you're including information the client doesn't need at this stage. Save the detail for the contract."
      },
      {
        type: "tip",
        text: "End every proposal with a clear call to action and a deadline. 'This proposal is valid until [date]. Reply to confirm and I'll send the contract.' Urgency without pressure."
      }
    ]
  },
  {
    id: "blog-6",
    slug: "dealing-with-difficult-clients",
    title: "Dealing With Difficult Clients Without Burning Bridges",
    excerpt: "Not every client relationship goes smoothly. Here's how to handle conflict professionally and protect your reputation.",
    author: "Kreator Editorial",
    date: "March 5, 2026",
    readTime: "7 min read",
    category: "Media Production",
    tags: ["Client Management", "Mindset", "Communication"],
    content: [
      {
        type: "paragraph",
        text: "Difficult clients come in many forms: the micromanager, the scope creeper, the late payer, the one who 'just has one more small change.' Knowing how to handle each type without losing your cool — or your income — is a core freelance skill."
      },
      {
        type: "heading",
        text: "Separate the Person From the Problem"
      },
      {
        type: "paragraph",
        text: "Most difficult client behaviour comes from anxiety, not malice. They're worried the project won't deliver what they need. Acknowledging that anxiety — 'I understand this is important to get right' — often defuses tension faster than any logical argument."
      },
      {
        type: "heading",
        text: "Move Everything to Email"
      },
      {
        type: "paragraph",
        text: "When a client relationship gets tense, stop communicating via WhatsApp or phone calls. Move everything to email. It creates a paper trail, slows down reactive responses, and forces both parties to be more considered in what they say."
      },
      {
        type: "tip",
        text: "After every call or meeting, send a follow-up email summarising what was discussed and agreed. 'Just confirming from our call: we agreed to X, Y, Z.' This protects you and keeps the client accountable."
      },
      {
        type: "heading",
        text: "Know When to Walk Away"
      },
      {
        type: "paragraph",
        text: "Some clients aren't worth keeping. If a client is consistently disrespectful, refuses to pay on time, or keeps moving the goalposts despite clear contracts, it's okay to end the relationship professionally. A short, factual email stating you're unable to continue the project is enough. No drama needed."
      }
    ]
  },
  {
    id: "blog-7",
    slug: "motion-design-freelance-getting-started",
    title: "Breaking Into Motion Design Freelance: A Realistic Guide",
    excerpt: "Motion design is one of the most in-demand creative skills right now. Here's how to go from student to paid freelancer.",
    author: "Kreator Editorial",
    date: "February 10, 2026",
    readTime: "9 min read",
    category: "Motion Graphics",
    tags: ["Motion Design", "Beginner", "Career"],
    content: [
      {
        type: "paragraph",
        text: "Motion design sits at the intersection of graphic design, animation, and storytelling. It's used everywhere — from app onboarding flows to broadcast titles to social media ads. And right now, demand is outpacing supply of skilled practitioners."
      },
      {
        type: "heading",
        text: "The Minimum Viable Skillset"
      },
      {
        type: "paragraph",
        text: "You don't need to master everything at once. Start with After Effects fundamentals: keyframing, easing, the graph editor, and basic expressions. Add a working knowledge of Illustrator for asset creation. That combination alone can get you your first paid gig."
      },
      {
        type: "heading",
        text: "Build a Reel, Not a Portfolio"
      },
      {
        type: "paragraph",
        text: "Motion designers are judged by their reel — a 60–90 second showreel of your best work. Every second counts. Open strong, cut to the beat, and only include work you're proud of. A tight 60-second reel beats a bloated 3-minute one every time."
      },
      {
        type: "tip",
        text: "If you don't have client work yet, create spec work for real brands. Animate a logo for a company you admire. Recreate a title sequence from a show. These demonstrate skill just as effectively as paid work."
      },
      {
        type: "heading",
        text: "Where to Find Motion Design Gigs"
      },
      {
        type: "paragraph",
        text: "Motionographer Jobs is the industry standard. Contra and Upwork have a steady flow of smaller projects. Instagram and LinkedIn are underrated — posting your work consistently builds an inbound pipeline over time. Many motion designers get their best clients through DMs, not job boards."
      },
      {
        type: "heading",
        text: "Rate Yourself Correctly"
      },
      {
        type: "paragraph",
        text: "Motion design commands higher rates than static design because it requires more time and skill. Entry-level motion designers in Southeast Asia typically charge SGD 50–80/hour. Mid-level practitioners charge SGD 100–150/hour. Don't undersell the complexity of what you do."
      }
    ]
  },
  {
    id: "blog-8",
    slug: "tax-basics-for-freelancers-singapore",
    title: "Tax Basics Every Singapore Freelancer Needs to Know",
    excerpt: "Taxes aren't exciting, but getting them wrong is expensive. Here's a plain-English breakdown of what you owe and when.",
    author: "Kreator Editorial",
    date: "January 30, 2026",
    readTime: "8 min read",
    category: "Media Production",
    tags: ["Tax", "Finance", "Singapore"],
    content: [
      {
        type: "paragraph",
        text: "Most freelancers in Singapore don't think about taxes until IRAS sends a letter. By then, you may have already underpaid — or worse, spent money you should have set aside. A little upfront knowledge saves a lot of stress."
      },
      {
        type: "heading",
        text: "You Are a Self-Employed Person (SEP)"
      },
      {
        type: "paragraph",
        text: "In Singapore, freelancers are classified as self-employed persons. You're responsible for declaring your own income and paying your own taxes. Unlike salaried employees, nothing is deducted at source — it's all on you."
      },
      {
        type: "heading",
        text: "What Counts as Income"
      },
      {
        type: "paragraph",
        text: "All money you receive for freelance work is taxable income — including cash payments, PayNow transfers, and payments in kind. Keep records of every transaction. IRAS can audit up to 5 years back."
      },
      {
        type: "tip",
        text: "Set aside 20% of every payment you receive into a separate savings account. This covers your income tax and CPF MediSave contributions. You'll thank yourself come filing season."
      },
      {
        type: "heading",
        text: "CPF MediSave Contributions"
      },
      {
        type: "paragraph",
        text: "As an SEP, you're required to contribute to CPF MediSave if your net trade income exceeds SGD 6,000 per year. The contribution rate varies by age. This is separate from income tax and is often overlooked by new freelancers."
      },
      {
        type: "heading",
        text: "Deductible Expenses"
      },
      {
        type: "paragraph",
        text: "You can deduct legitimate business expenses from your taxable income — software subscriptions, equipment, co-working space, professional development courses. Keep receipts for everything. These deductions can meaningfully reduce your tax bill."
      }
    ]
  },
  {
    id: "blog-9",
    slug: "client-onboarding-process-that-sets-you-apart",
    title: "The Client Onboarding Process That Sets You Apart",
    excerpt: "How you start a project determines how it ends. A strong onboarding process builds trust, sets expectations, and prevents 90% of common problems.",
    author: "Kreator Editorial",
    date: "March 20, 2026",
    readTime: "6 min read",
    category: "UX/UI & Web Design",
    tags: ["Client Management", "Workflow", "Professionalism"],
    content: [
      {
        type: "paragraph",
        text: "The first week of a client relationship sets the tone for everything that follows. Freelancers who invest in a proper onboarding process have fewer disputes, get paid faster, and receive better reviews. It's not overhead — it's insurance."
      },
      {
        type: "heading",
        text: "Send a Welcome Packet"
      },
      {
        type: "paragraph",
        text: "After a client signs the contract and pays the deposit, send them a welcome packet. This should include: a project timeline, your communication preferences (email only, response within 24 hours), what you need from them to start, and a brief FAQ covering common questions."
      },
      {
        type: "heading",
        text: "Run a Kickoff Call"
      },
      {
        type: "paragraph",
        text: "A 30-minute kickoff call at the start of every project is worth its weight in gold. Use it to align on goals, clarify the brief, and establish rapport. Clients who feel heard at the start are far less likely to become difficult later."
      },
      {
        type: "tip",
        text: "Record your kickoff calls (with permission) and send a written summary afterwards. This becomes your reference document if scope disputes arise later."
      },
      {
        type: "heading",
        text: "Set Up a Shared Workspace"
      },
      {
        type: "paragraph",
        text: "Give clients a single place to see project status, leave feedback, and access deliverables. A simple Notion page or Google Drive folder works fine. The goal is to eliminate 'where are we at?' emails and give clients a sense of control without requiring constant updates from you."
      }
    ]
  },
  {
    id: "blog-10",
    slug: "burnout-prevention-for-creative-freelancers",
    title: "Burnout Prevention for Creative Freelancers",
    excerpt: "Freelancing can feel like freedom — until it doesn't. Here's how to build a sustainable practice that doesn't hollow you out.",
    author: "Kreator Editorial",
    date: "February 14, 2026",
    readTime: "7 min read",
    category: "Motion Graphics",
    tags: ["Mindset", "Health", "Sustainability"],
    content: [
      {
        type: "paragraph",
        text: "Burnout in freelancing looks different from burnout in a regular job. There's no HR department to notice, no sick leave to take, and no manager to tell you to slow down. By the time most freelancers recognise it, they're already deep in it."
      },
      {
        type: "heading",
        text: "The Warning Signs"
      },
      {
        type: "paragraph",
        text: "Watch for: dreading work you used to enjoy, declining quality in your output, snapping at clients over small things, working longer hours for less output, and a persistent feeling that you're always behind. These are signals, not character flaws."
      },
      {
        type: "heading",
        text: "Cap Your Billable Hours"
      },
      {
        type: "paragraph",
        text: "Decide in advance how many billable hours you'll work per week and treat it as a hard limit. Most creatives do their best work in 4–5 focused hours per day. Working 10-hour days doesn't double your output — it halves your quality and triples your recovery time."
      },
      {
        type: "tip",
        text: "Schedule at least one full non-work day per week. Not a 'light work' day — a genuinely offline day. Your creativity needs fallow time to regenerate."
      },
      {
        type: "heading",
        text: "Raise Your Rates"
      },
      {
        type: "paragraph",
        text: "Many freelancers burn out not from too much work, but from too much low-paying work. Raising your rates means you can work fewer hours for the same income. It also filters out the clients who cause the most stress. Higher rates and better clients are two sides of the same coin."
      },
      {
        type: "heading",
        text: "Build Non-Billable Time Into Your Week"
      },
      {
        type: "paragraph",
        text: "Spend time each week on work that isn't for a client — personal projects, skill development, creative exploration. This is what keeps the creative well full. Freelancers who only ever work for clients eventually run out of ideas to bring to those clients."
      }
    ]
  }
];
